import type { MacroVariable } from "@redotech/redo-support-common/macro";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import type { ShortcutBinding } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { memo, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as variableAutocompleteCss from "./variable-autocomplete.module.css";
import { VariableList } from "./variable-list";

export interface VariableAutocompleteProps {
  open: boolean;
  variables: readonly MacroVariable[];
  focusedIndex: number;
  anchorRect: DOMRect | null;
  setFocusedIndex: (index: number) => void;
  onSelect: (variable: MacroVariable) => void;
  onClose: () => void;
}

export const VariableAutocomplete = memo(function VariableAutocomplete({
  open,
  variables,
  focusedIndex,
  anchorRect,
  setFocusedIndex,
  onSelect,
  onClose,
}: VariableAutocompleteProps) {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const bindings = useMemo<ShortcutBinding[]>(() => {
    const selectFocused = () => {
      const variable = variables[focusedIndex];
      if (variable) {
        onSelect(variable);
      }
    };
    return [
      {
        shortcut: {
          code: "ArrowDown",
          keyLabel: "↓",
          scope: "variableAutocomplete",
          allowInEditor: true,
        },
        handler: () =>
          setFocusedIndex(Math.min(focusedIndex + 1, variables.length - 1)),
      },
      {
        shortcut: {
          code: "ArrowUp",
          keyLabel: "↑",
          scope: "variableAutocomplete",
          allowInEditor: true,
        },
        handler: () => setFocusedIndex(Math.max(focusedIndex - 1, 0)),
      },
      {
        shortcut: {
          code: "Enter",
          keyLabel: "Enter",
          scope: "variableAutocomplete",
          allowInEditor: true,
        },
        handler: selectFocused,
      },
      {
        shortcut: {
          code: "Tab",
          keyLabel: "Tab",
          scope: "variableAutocomplete",
          allowInEditor: true,
        },
        handler: selectFocused,
      },
    ];
  }, [focusedIndex, variables, setFocusedIndex, onSelect]);

  useKeyboardShortcuts({
    bindings,
    scope: "variableAutocomplete",
    enabled: open,
    onEscape: onClose,
  });

  // The caret anchor is `position: fixed` with viewport coords from the
  // current selection. RedoModal wraps its surface in `transform:
  // translate(-50%, -50%)` for centering — a CSS transform on an ancestor
  // breaks `position: fixed` for descendants (they become positioned relative
  // to the transformed ancestor instead of the viewport). Portaling the
  // anchor div to `document.body` escapes that transform chain so the rect
  // we set actually lands at the caret. MentionAutocomplete works without
  // this because it renders inside a panel with no transformed ancestor.
  return (
    <>
      {typeof document !== "undefined" &&
        createPortal(
          <div
            aria-hidden
            className={variableAutocompleteCss.caretAnchor}
            ref={setContainerRef}
            style={{
              top: anchorRect?.top ?? 0,
              left: anchorRect?.left ?? 0,
              height: anchorRect?.height ?? 0,
            }}
          />,
          document.body,
        )}
      <Dropdown
        anchor={containerRef}
        constrainHeight
        fitToAnchor={false}
        flexProps={{ p: "none" }}
        flip
        open={open && !!anchorRect}
        placement="bottom-start"
      >
        <Flex
          className={variableAutocompleteCss.dropdownContent}
          dir="column"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          <VariableList
            focusedIndex={focusedIndex}
            onSelect={onSelect}
            refToListenTo={containerRef}
            setFocusedIndex={(index) => {
              const next =
                typeof index === "function" ? index(focusedIndex) : index;
              if (next === undefined) {
                return;
              }
              setFocusedIndex(next);
            }}
            variables={variables}
          />
        </Flex>
      </Dropdown>
    </>
  );
});
